import React, { useState, useEffect, useContext } from "react";
import {
  TablePagination,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  Grid,
  Alert,
  Card,
  CardContent,
  Avatar,
  Chip,
  Fade,
  Zoom,
  Tooltip,
  Container,
  InputAdornment,
  Divider,
  Badge,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  ElectricCar as ElectricCarIcon,
  Speed as SpeedIcon,
  Person as PersonIcon,
  BatteryChargingFull as BatteryIcon,
} from "@mui/icons-material";
import { useMovilidadElectrica } from "../../context/MovilidadElectricaContext";
import { AuthContext } from "../../context/AuthContext";
import { getPlacesByUserName } from "../../services/userService";
import { getVehiclesByUserAndPlace } from "../../services/vehicleService";
import CreateVehiculoModal from "./CreateVehiculoModal";
import EditVehiculoModal from "./EditVehiculoModal";
import DeleteVehiculoModal from "./DeleteVehiculoModal";

const VehiculosRegistradosList = () => {
  const { vehicles: adminVehicles, refreshVehicles, loading: adminLoading, error: adminError } = useMovilidadElectrica();
  const { user } = useContext(AuthContext);
  const isAdminUser = user?.is_admin;
  const [userVehicles, setUserVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      setError(null);
      if (isAdminUser) {
        const params = {
          page: currentPage + 1,
          per_page: rowsPerPage,
          searchTerm: searchTerm,
        };
        await refreshVehicles(params);
        setLoading(false); // El loading del contexto se usa para la UI
      } else {
        try {
          const userDetailsResponse = await getPlacesByUserName();
          const userDetails = userDetailsResponse.data[0];
          if (userDetails && userDetails.id && userDetails.places && userDetails.places.length > 0) {
            const userId = userDetails.id;
            const placeId = userDetails.places[0].id;
            const vehiclesData = await getVehiclesByUserAndPlace({ user_id: userId, place_id: placeId });
            setUserVehicles(vehiclesData.data);
          }
        } catch (err) {
          console.error("Error fetching user vehicles:", err);
          setError("No se pudieron cargar los vehículos.");
        } finally {
          setLoading(false);
        }
      }
    };

    const handler = setTimeout(() => {
      fetchVehicles();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [isAdminUser, searchTerm, currentPage, rowsPerPage, refreshVehicles]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0);
  };

  const handleCreateClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditClick = (vehiculo) => {
    setSelectedVehiculo(vehiculo);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (vehiculo) => {
    setSelectedVehiculo(vehiculo);
    setIsDeleteModalOpen(true);
  };

  const handleCloseModal = (shouldRefresh = false) => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedVehiculo(null);
    if (shouldRefresh) {
      window.location.reload();
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const vehiclesToDisplay = isAdminUser 
    ? adminVehicles.data 
    : (userVehicles || []).slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
  const totalCount = isAdminUser ? adminVehicles.total : (userVehicles ? userVehicles.length : 0);

  // Función para obtener el ícono del tipo de cargador
  const getChargerTypeIcon = (chargerType) => {
    switch (chargerType?.toLowerCase()) {
      case 'ac':
        return <BatteryIcon sx={{ color: '#4CAF50' }} />;
      case 'dc':
        return <BatteryIcon sx={{ color: '#FF9800' }} />;
      default:
        return <BatteryIcon sx={{ color: '#75787B' }} />;
    }
  };

  // Función para obtener el color del chip según el tipo de cargador
  const getChargerTypeColor = (chargerType) => {
    switch (chargerType?.toLowerCase()) {
      case 'ac':
        return '#4CAF50';
      case 'dc':
        return '#FF9800';
      default:
        return '#75787B';
    }
  };

  // Componente VehicleCard
  const VehicleCard = ({ vehicle, index }) => (
    <Fade in={true} timeout={300 + index * 100}>
      <Card
        sx={{
          borderRadius: { xs: 2, sm: 3 },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: { xs: 'none', sm: 'translateY(-4px)' },
            boxShadow: { xs: 3, sm: 8 }
          },
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header del vehículo - Layout responsive */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            justifyContent: 'space-between', 
            mb: { xs: 1.5, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            {/* Información principal del vehículo */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1.5, sm: 2 }, 
              flex: 1,
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Avatar
                src={vehicle.photo}
                alt={`Foto de ${vehicle.plate}`}
                sx={{
                  width: { xs: 50, sm: 60, md: 80 },
                  height: { xs: 50, sm: 60, md: 80 },
                  border: '3px solid #E0E0E0',
                  background: 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
                  flexShrink: 0
                }}
              >
                <ElectricCarIcon sx={{ 
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }, 
                  color: '#75787B' 
                }} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#002642',
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                    mb: 0.5,
                    lineHeight: 1.2
                  }}
                >
                  {vehicle.plate}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#75787B',
                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                    mb: { xs: 1, sm: 1.5 },
                    lineHeight: 1.3
                  }}
                >
                  {vehicle.model}
                </Typography>
              </Box>
            </Box>

            {/* Botones de acción - Responsive layout */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'row', sm: 'column' }, 
              gap: { xs: 1, sm: 1 },
              justifyContent: { xs: 'flex-end', sm: 'flex-start' },
              alignSelf: { xs: 'flex-end', sm: 'flex-start' }
            }}>
              <Tooltip title="Editar vehículo">
                <IconButton
                  onClick={() => handleEditClick(vehicle)}
                  sx={{
                    color: '#002642',
                    backgroundColor: 'rgba(0, 38, 66, 0.1)',
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    '&:hover': {
                      backgroundColor: '#002642',
                      color: '#FFFFFF',
                      transform: { xs: 'scale(1.05)', sm: 'scale(1.1)' }
                    }
                  }}
                  size="small"
                >
                  <EditIcon sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar vehículo">
                <IconButton
                  onClick={() => handleDeleteClick(vehicle)}
                  sx={{
                    color: '#F44336',
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    '&:hover': {
                      backgroundColor: '#F44336',
                      color: '#FFFFFF',
                      transform: { xs: 'scale(1.05)', sm: 'scale(1.1)' }
                    }
                  }}
                  size="small"
                >
                  <DeleteIcon sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Notificaciones">
                <IconButton
                  sx={{
                    color: '#DB9600',
                    backgroundColor: 'rgba(219, 150, 0, 0.1)',
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    '&:hover': {
                      backgroundColor: '#DB9600',
                      color: '#FFFFFF',
                      transform: { xs: 'scale(1.05)', sm: 'scale(1.1)' }
                    }
                  }}
                  size="small"
                >
                  <Badge badgeContent={1} color="error">
                    <NotificationsIcon sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Chips informativos - Layout responsive */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, sm: 1 }, 
            flexWrap: 'wrap',
            mt: 'auto'
          }}>
            <Chip
              icon={getChargerTypeIcon(vehicle.charger_type)}
              label={`Cargador ${vehicle.charger_type?.toUpperCase() || 'N/A'}`}
              size="small"
              sx={{
                backgroundColor: `${getChargerTypeColor(vehicle.charger_type)}15`,
                color: getChargerTypeColor(vehicle.charger_type),
                borderColor: getChargerTypeColor(vehicle.charger_type),
                border: '1px solid',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8125rem' },
                height: { xs: 24, sm: 28, md: 32 },
                '& .MuiChip-icon': {
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
                }
              }}
            />
            <Chip
              icon={<PersonIcon sx={{ color: '#002642', fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }} />}
              label={`Usuario: ${vehicle.user_id}`}
              size="small"
              variant="outlined"
              sx={{
                borderColor: '#002642',
                color: '#002642',
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8125rem' },
                height: { xs: 24, sm: 28, md: 32 }
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, md: 3, lg: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: '#FAFAFA'
      }}
    >
      {/* Header moderno con gradiente */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
          borderRadius: { xs: 2, sm: 3, md: 4 },
          p: { xs: 2, sm: 3, md: 4, lg: 5 },
          mb: { xs: 2, sm: 3, md: 4 },
          color: '#FFFFFF',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: { xs: 2, md: 4 }
        }}
      >
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            right: 0,
            width: { xs: 60, md: 100 },
            height: { xs: 60, md: 100 },
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            transform: 'translate(50%, -50%)'
          }}
        />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          gap: { xs: 2, sm: 3, md: 0 },
          position: 'relative' 
        }}>
          <Box sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                mb: { xs: 0.5, sm: 1 }, 
                color: '#FFFFFF',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                lineHeight: 1.2
              }}
            >
              Gestión de Vehículos
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem', lg: '1.25rem' },
                flexWrap: 'wrap'
              }}
            >
              <ElectricCarIcon sx={{ color: '#FFFFFF', fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
              <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                Administra la flota de vehículos eléctricos
              </Box>
            </Typography>
          </Box>
          <Button 
            variant="contained"
            size={window.innerWidth < 600 ? "medium" : "large"}
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            sx={{ 
              backgroundColor: '#DB9600',
              color: '#FFFFFF',
              fontWeight: 700,
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
              borderRadius: 3,
              boxShadow: 3,
              border: '2px solid #DB9600',
              minWidth: { xs: '100%', sm: '200px', md: 'auto' },
              fontSize: { xs: '0.875rem', md: '1rem' },
              alignSelf: { xs: 'stretch', sm: 'flex-start', md: 'center' },
              '&:hover': {
                backgroundColor: '#B87900',
                borderColor: '#B87900',
                color: '#FFFFFF',
                transform: 'translateY(-2px)',
                boxShadow: 6
              }
            }}
          >
            Registrar Vehículo
          </Button>
        </Box>
      </Box>

      {/* Barra de búsqueda mejorada */}
      <Card sx={{ mb: { xs: 2, sm: 3, md: 4 }, borderRadius: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={8} lg={8}>
              <TextField
                variant="outlined"
                placeholder="Buscar por placa del vehículo..."
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                size={window.innerWidth < 600 ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#75787B', fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                    '& .MuiInputBase-input': {
                      py: { xs: 1.5, md: 2 }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 2 },
                justifyContent: { xs: 'space-between', md: 'flex-end' },
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' }
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#75787B', 
                    fontWeight: 500,
                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                    textAlign: { xs: 'left', sm: 'right' },
                    order: { xs: 2, sm: 1 }
                  }}
                >
                  {vehiclesToDisplay?.length || 0} vehículo{(vehiclesToDisplay?.length || 0) !== 1 ? 's' : ''} encontrado{(vehiclesToDisplay?.length || 0) !== 1 ? 's' : ''}
                </Typography>
                <Chip 
                  icon={<CarIcon sx={{ color: '#FFFFFF', fontSize: { xs: '0.9rem', md: '1rem' } }} />}
                  label={`Total: ${totalCount}`}
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  sx={{
                    backgroundColor: '#002642',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    border: '1px solid #002642',
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8125rem' },
                    order: { xs: 1, sm: 2 }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lista de vehículos como cards */}
      {loading || adminLoading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : error ? (
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: 3, 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '1.1rem'
            }
          }}
        >
          {error}
        </Alert>
      ) : adminError ? (
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: 3, 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '1.1rem'
            }
          }}
        >
          Error al cargar vehículos: {adminError.message}
        </Alert>
      ) : !vehiclesToDisplay || vehiclesToDisplay.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: 'center', p: 6 }}>
          <ElectricCarIcon sx={{ fontSize: 64, color: '#75787B', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#002642', mb: 2, fontWeight: 600 }}>
            No se encontraron vehículos
          </Typography>
          <Typography variant="body1" sx={{ color: '#75787B', mb: 3 }}>
            {searchTerm ? 'Prueba con otro término de búsqueda' : 'Comienza registrando tu primer vehículo'}
          </Typography>
          <Button 
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            sx={{
              backgroundColor: '#002642',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#00577A'
              }
            }}
          >
            Registrar Vehículo
          </Button>
        </Card>
      ) : (
        <>
          <Grid 
            container 
            spacing={{ xs: 2, sm: 2.5, md: 3, lg: 3 }}
            sx={{ 
              px: { xs: 0, sm: 0 },
              mb: { xs: 2, sm: 3 }
            }}
          >
            {vehiclesToDisplay.map((vehicle, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                lg={4} 
                xl={3} 
                key={vehicle.vehicle_id}
                sx={{
                  display: 'flex',
                  '& > *': {
                    width: '100%'
                  }
                }}
              >
                <VehicleCard vehicle={vehicle} index={index} />
              </Grid>
            ))}
          </Grid>
          
          {/* Paginación responsive */}
          {totalCount > rowsPerPage && (
            <Card sx={{ 
              mt: { xs: 3, sm: 4 }, 
              borderRadius: { xs: 2, sm: 3 },
              overflow: 'hidden'
            }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={currentPage}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[8, 12, 16, 24]}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `+${to}`}`
                }
                sx={{ 
                  '& .MuiTablePagination-toolbar': {
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    minHeight: { xs: 'auto', sm: 64 }
                  },
                  '& .MuiTablePagination-selectLabel': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    color: '#75787B',
                    fontWeight: 500,
                    mb: { xs: 1, sm: 0 }
                  },
                  '& .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    color: '#75787B',
                    fontWeight: 500,
                    order: { xs: -1, sm: 0 },
                    mb: { xs: 1, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                    textAlign: { xs: 'center', sm: 'left' }
                  },
                  '& .MuiTablePagination-select': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    borderRadius: 2,
                    backgroundColor: 'rgba(0, 38, 66, 0.05)',
                    border: '1px solid rgba(0, 38, 66, 0.2)',
                    '&:focus': {
                      backgroundColor: 'rgba(0, 38, 66, 0.1)',
                      borderColor: '#002642'
                    }
                  },
                  '& .MuiTablePagination-actions': {
                    ml: { xs: 0, sm: 2 },
                    '& .MuiIconButton-root': {
                      color: '#002642',
                      borderRadius: 2,
                      mx: 0.25,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 38, 66, 0.1)'
                      },
                      '&.Mui-disabled': {
                        color: '#BDBDBD'
                      }
                    }
                  },
                  '& .MuiInputBase-root': {
                    '&:before, &:after': {
                      display: 'none'
                    }
                  }
                }}
              />
            </Card>
          )}
        </>
      )}

      {/* Modales */}
      <CreateVehiculoModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
      />
      <EditVehiculoModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        vehiculo={selectedVehiculo}
      />
      <DeleteVehiculoModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        vehiculo={selectedVehiculo}
      />
    </Container>
  );
};export default VehiculosRegistradosList;
